import { useState } from "react";
import { toast } from "sonner";
import { HandshakeIcon } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { tagCto, untagCto } from "../lib/api";

export const CtoBadge = ({ cto, testid }) => {
  if (!cto?.status) return null;
  const confirmed = cto.status === "confirmed";
  return (
    <span
      data-testid={testid}
      title={(cto.signals || []).join(" · ") || "Community Take Over"}
      className="text-[9px] font-mono px-1 py-0.5 tracking-wide"
      style={
        confirmed
          ? { background: "rgba(0,229,153,0.15)", border: "1px solid rgba(0,229,153,0.6)", color: "#00E599" }
          : { background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.2)", color: "#9aa4b2" }
      }
    >
      🤝 {confirmed ? "CTO" : "CTO?"}
    </span>
  );
};

export const CtoPanel = ({ coin, onChange }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [busy, setBusy] = useState(false);
  const cto = coin.cto;
  const tagged = cto?.status === "confirmed";

  if (!cto && !isAdmin) return null;

  const run = async (fn, ok) => {
    setBusy(true);
    try {
      await fn();
      toast.success(ok);
      onChange?.();
    } catch {
      toast.error("Action failed");
    } finally {
      setBusy(false);
    }
  };

  const borderCol = tagged ? "rgba(0,229,153,0.4)" : "rgba(255,255,255,0.12)";
  const bg = tagged ? "rgba(0,229,153,0.05)" : "transparent";

  return (
    <div data-testid="cto-panel" className="border p-4 sm:p-5" style={{ borderColor: borderCol, background: bg }}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <HandshakeIcon size={24} weight="fill" className="text-neon shrink-0" />
          <div>
            <div className="font-heading font-bold text-lg flex items-center gap-2 flex-wrap">
              Community Take Over
              {tagged && <span className="text-[10px] font-mono px-1.5 py-0.5 border border-neon/50 text-neon">CONFIRMED</span>}
              {cto?.status === "possible" && <span className="text-[10px] font-mono px-1.5 py-0.5 border border-white/20 text-muted-foreground">POSSIBLE</span>}
              {!cto && <span className="text-[10px] font-mono px-1.5 py-0.5 border border-white/20 text-muted-foreground">NOT FLAGGED</span>}
            </div>
            <p className="text-xs text-muted-foreground mt-0.5 max-w-md">
              {cto
                ? "The original dev has stepped back and the community is running this token."
                : "Not flagged as a community takeover yet."}
            </p>
            {cto?.since?.multiple != null && (
              <div data-testid="cto-since" className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-mono px-2 py-0.5 border"
                style={cto.since.multiple >= 1
                  ? { background: "rgba(0,229,153,0.12)", borderColor: "rgba(0,229,153,0.5)", color: "#00E599" }
                  : { background: "rgba(255,51,102,0.12)", borderColor: "rgba(255,51,102,0.5)", color: "#FF3366" }}>
                {cto.since.multiple >= 1 ? `▲ ${cto.since.multiple}× since flagged` : `▼ ${cto.since.pct}% since flagged`}
              </div>
            )}
          </div>
        </div>
        {isAdmin &&
          (tagged ? (
            <button
              data-testid="cto-untag-btn"
              disabled={busy}
              onClick={() => run(() => untagCto(coin.address), "CTO tag removed")}
              className="px-3 py-1.5 text-xs border border-white/20 hover:border-danger/50 hover:text-danger transition-colors disabled:opacity-50"
            >
              Remove CTO tag
            </button>
          ) : (
            <button
              data-testid="cto-tag-btn"
              disabled={busy}
              onClick={() => run(() => tagCto({ mint: coin.address, symbol: coin.symbol, name: coin.name }), "Marked as CTO")}
              className="px-3 py-1.5 text-xs bg-neon text-black font-semibold hover:bg-neon/90 transition-colors disabled:opacity-50"
            >
              Mark as CTO
            </button>
          ))}
      </div>
      {cto?.signals?.length > 0 && (
        <ul className="mt-3 space-y-1">
          {cto.signals.map((s, i) => (
            <li key={i} className="text-xs text-muted-foreground flex items-start gap-1.5">
              <span className="text-neon mt-0.5">•</span> {s}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
